import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, asyncScheduler, of, throwError } from 'rxjs';
import { catchError, filter, map, retry, switchMap } from 'rxjs/operators';

import { ApiService } from '@core/services/api/api.service';
import { DeviceService } from '@core/services/device/device.service';
import { ProductsService } from '@core/services/products/products.service';
import { WipeDataService } from '@core/services/wipe-data/wipe-data.service';

import { IAuthData } from '@shared/models/auth.model';
import { IDigestData } from '@shared/models/digest.model';
import { AuthService } from '@shared/services/auth.service';
import { DigestService } from '@shared/services/digest.service';

@Injectable({ providedIn: 'root' })
export class AuthInterceptor implements HttpInterceptor {
  private EXCLUDED_STATUSES = new Set([0, 400, 403, 404, 406, 500, 502]);
  constructor(
    private authService: AuthService,
    private digestService: DigestService,
    private deviceService: DeviceService,
    private productsService: ProductsService,
    private wipeDataService: WipeDataService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let authDataCache: IAuthData | undefined;
    if (!req.url.includes('rest/personal')) {
      return next.handle(req);
    }
    return this.authService.getAuthData().pipe(
      map((authData) => {
        authDataCache = authData;
        return this.prepareHeaders(req.method, req.url, authData);
      }),
      switchMap((headers) => {
        const copyRequest = req.clone({
          headers
        });
        return next.handle(copyRequest);
      }),
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse) {
          if (this.EXCLUDED_STATUSES.has(err.status)) {
            return throwError(err);
          }

          // 3. Mark to repopulate nonce, salt and other auth data from the "WWW-Authenticate" header
          ApiService.errorResponse = err;

          // 4. Check if there's a "salt" in the "WWW-Authenticate" header
          const isThereSalt = new RegExp('salt', 'g').test(err.headers.get('WWW-Authenticate'));

          // 5. Check "deviceIsNotRegistry" error
          if (this.deviceService.checkDeviceIsNotRegistered(err) && authDataCache.name) {
            ApiService.errorResponse = undefined;
            if (err.error.errorCode !== '1009') {
              this.productsService.refreshProducts$.next(true);
              this.wipeDataService.wipe({ appSettingExcludedKeys: ['enableNotification'] });
              this.authService.clearAuthData();
            }
            return throwError('deviceIsNotRegistry');
          }

          // 6. If we have 1010 code, and it's not login process
          // - Mark that we want to get new auth data (nonce, salt, etc.)
          //   in a "WWW-Authenticate" header with the next request
          if (
            err.error &&
            err.error.errorCode == '1010' &&
            !isThereSalt
            // && !(new RegExp('rest\/personal/ping', 'g')).test(err.url)
            // && !(new RegExp('rest\/personal\/client_info', 'g')).test(err.url)
          ) {
            ApiService.errorResponse = null;
          }

          // 7. Populate the request with headers
          // - If we (6) then repopulate saved auth data and make headers from it.
          // - If we not (6) then just make headers from saved earlier auth data.
          const copyRequest = req.clone({
            headers: this.prepareHeaders(req.method, new URL(req.url).pathname.substring(1), authDataCache)
          });

          // n. Handle the request
          return next.handle(copyRequest);
        }
      })
    );
  }

  private prepareHeaders(method: string, uri: string, authData: IAuthData): HttpHeaders {
    const digestData: IDigestData = {
      error: ApiService.errorResponse,
      method,
      uri,
      authData
    };
    console.log(uri);
    const authorizationHeaderContent = ApiService.errorResponse
      ? this.digestService.generateDigestHeaderFromError(digestData)
      : this.digestService.getFirstAuthorizationHeader(authData);
    return new HttpHeaders({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Authorization: authorizationHeaderContent,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      // 'Content-Type': 'application/json',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Cache-control': 'no-store',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Expires: '0',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Pragma: 'no-cache'
    });
  }
}
